import { apiClient } from './client';
import type { ApiResponse, PagedResult } from '@/types/api.types';
import type {
  BankDepositDto,
  BankDepositListDto,
  BankDepositFilterDto,
  CreateBankDepositRequest,
  UpdateBankDepositRequest,
  CashDueRowDto,
  CashAccountOptionDto,
  CashToDepositRowDto,
  UnbankedCashAgeRowDto,
  CreateCashDepositRequest,
  CashDepositResultDto,
} from '@/types/bankDeposit.types';

export const bankDepositApi = {
  /** GET /api/v1/bank-deposits — paginated list */
  getList: (filter?: BankDepositFilterDto) =>
    apiClient.get<ApiResponse<PagedResult<BankDepositListDto>>>('/bank-deposits', { params: filter }),

  /** GET /api/v1/bank-deposits/cash-due — cash collected by location/date, still un-banked (GAP-016) */
  getCashDue: (params?: { locationId?: string; dateFrom?: string; dateTo?: string }) =>
    apiClient.get<ApiResponse<CashDueRowDto[]>>('/bank-deposits/cash-due', { params }),

  /** GET /api/v1/bank-deposits/cash-in-hand — posted balance of one till/cash account up to a date (424363) */
  getCashInHand: (params: { accountId: string; locationId?: string; asOf: string }) =>
    apiClient.get<ApiResponse<{ balance: number }>>('/bank-deposits/cash-in-hand', { params }),

  /** GET /api/v1/bank-deposits/cash-accounts: cash in hand accounts a deposit can come from */
  getCashAccounts: () =>
    apiClient.get<ApiResponse<CashAccountOptionDto[]>>('/bank-deposits/cash-accounts'),

  /** GET /api/v1/bank-deposits/cash-to-deposit: the Cash Deposit grid for one cash account */
  getCashToDeposit: (params: {
    cashAccountId: string; costCenterId?: string; asOf?: string;
    dateFrom?: string; dateTo?: string; includeDeposited?: boolean;
  }) =>
    apiClient.get<ApiResponse<CashToDepositRowDto[]>>('/bank-deposits/cash-to-deposit', { params }),

  /** GET /api/v1/bank-deposits/cash-to-deposit/ageing: unbanked cash by age, per cost centre */
  getUnbankedCashAgeing: (params: { cashAccountId: string; costCenterId?: string; asOf?: string }) =>
    apiClient.get<ApiResponse<UnbankedCashAgeRowDto[]>>('/bank-deposits/cash-to-deposit/ageing', { params }),

  /** POST /api/v1/bank-deposits/cash-deposit: save the grid as one deposit (posts it when allowed) */
  createCashDeposit: (data: CreateCashDepositRequest) =>
    apiClient.post<ApiResponse<CashDepositResultDto>>('/bank-deposits/cash-deposit', data),

  /** GET /api/v1/bank-deposits/{id} — full deposit detail with lines */
  getById: (id: string) =>
    apiClient.get<ApiResponse<BankDepositDto>>(`/bank-deposits/${id}`),

  /** POST /api/v1/bank-deposits — create new deposit (Draft) */
  create: (data: CreateBankDepositRequest) =>
    apiClient.post<ApiResponse<{ id: string }>>('/bank-deposits', data),

  /** PUT /api/v1/bank-deposits/{id} — update draft deposit */
  update: (id: string, data: UpdateBankDepositRequest) =>
    apiClient.put<ApiResponse<object>>(`/bank-deposits/${id}`, data),

  /** POST /api/v1/bank-deposits/{id}/submit — submit for approval */
  submit: (id: string) =>
    apiClient.post<ApiResponse<object>>(`/bank-deposits/${id}/submit`),

  /** POST /api/v1/bank-deposits/{id}/approve — approve the deposit */
  approve: (id: string, remarks?: string) =>
    apiClient.post<ApiResponse<object>>(`/bank-deposits/${id}/approve`, { remarks }),

  /** POST /api/v1/bank-deposits/{id}/reject — reject with reason */
  reject: (id: string, reason: string) =>
    apiClient.post<ApiResponse<object>>(`/bank-deposits/${id}/reject`, { reason }),

  /** POST /api/v1/bank-deposits/{id}/post — post to general ledger */
  post: (id: string, contraGLAccountId?: string | null) =>
    apiClient.post<ApiResponse<object>>(`/bank-deposits/${id}/post`, { contraGLAccountId }),

  /** POST /api/v1/bank-deposits/{id}/reverse — create reversal deposit */
  reverse: (id: string, reversalDate: string, reversalPostingDate: string, narration?: string) =>
    apiClient.post<ApiResponse<{ id: string }>>(`/bank-deposits/${id}/reverse`, {
      reversalDate,
      reversalPostingDate,
      narration,
    }),

  /** POST /api/v1/bank-deposits/{id}/cancel — cancel the deposit */
  cancel: (id: string, reason?: string) =>
    apiClient.post<ApiResponse<object>>(`/bank-deposits/${id}/cancel`, { reason }),

  /** DELETE /api/v1/bank-deposits/{id} — soft-delete (draft only) */
  delete: (id: string) =>
    apiClient.delete<ApiResponse<object>>(`/bank-deposits/${id}`),
};
